// ─────────────────────────────────────────────────────────────
// SINGLE SOURCE OF TRUTH for portfolio content.
// Ujjwal: update everything here (chapters, skills, hobbies,
// links, quote) and the whole site (DOM + 3D world) follows.
// Placeholders are wrapped in [square brackets] on purpose.
// ─────────────────────────────────────────────────────────────

export const profile = {
    firstName: 'Ujjwal',
    lastName: 'Praveen',
    roles: 'Test Lead @ TOYOW · CEO & Co-founder @ Worklyne',
    tagline: 'From curiosity, to knowledge, to implementation.',
    intro:
        'A self-taught QA mind that left the linear path for the corporate one, through HRMS and payroll logic into DeFi application and blockchain testing, and then started building the workspace management platform the chaos demanded. Every node on this path is a building. Scroll and walk it.',
    scrollCue: 'Scroll to walk the path',
};

export const chapters = [
    {
        id: 'genesis',
        stage: '00',
        title: 'Genesis & Roots',
        yearLabel: '1999 · The Seed',
        building: 'Origin Beacon: a monolith with a glowing core',
        description:
            'Before software, there was a heart process, inculcated from the very beginning: one step at a time. I was just like any other kid: chasing cartoons, playing outside, getting lost in simple joys, just a bit calmer, with a head full of quiet curiosity. Instead of playing with a toy, I wanted to sit with it, break it apart just to see how it works, and stitch it back together even if it turns out to be entirely different. That wiring was planted early, and it lay waiting until it found the right people to grow around.',
        tags: ['Origin: 1999', 'One Step At A Time', 'Reverse Engineering First', 'Curiosity Engine'],
        accent: '#9CA3AF',
    },
    {
        id: 'schooling',
        stage: '01',
        title: 'Foundational Schooling',
        yearLabel: 'High School by 2015 · Intermediate 2017',
        building: 'Academic Courtyard: debate halls & assembly stages',
        description:
            'I completed my high school by 2015 and my intermediate by 2017 at City Montessori School. This is where the genesis wiring took shape, surrounded by people. MUNs and debates burned off stage fear and built real confidence, and every new face carried a new mindset worth understanding. Break the topic apart, learn its insides, present it one step at a time: reverse engineering, now with an audience.',
        extras: [
            {
                label: 'On the Field',
                text: 'Football, table tennis, cricket and volleyball were my favourite things to do. Playing around with friends took up a lot of my time in school.',
            },
            {
                label: 'First Contact with Code',
                text: 'Computers entered early: editing things and a logo, writing my first code in Java, and learning hardware, software and how machines actually work. It became the quiet foundation for everything IT that came after.',
            },
        ],
        tags: ['City Montessori School', 'High School by 2015 · Intermediate 2017', 'MUNs & Debates', 'New People, New Mindsets'],
        accent: '#00F2FE',
    },
    {
        id: 'higher_edu',
        stage: '02',
        title: 'Higher Education',
        yearLabel: '2020 – 2022 · Chitkara University',
        building: 'Tech Workshop: scaffolded laboratory node',
        description:
            'Life happened, and Chitkara University became the next node. The university lifestyle itself was fascinating, a whole world of things that actually existed out there but had never been introduced to me. The journey with software and products started here. I was never the most brilliant coder in the room, but I ran on an ideology that fit the oldest wiring: see how things work, understand why they are required, then map the steps needed to actually implement them.',
        extras: [
            {
                label: 'Beyond the Classroom',
                text: 'Discipline found a stage. As a founding member of an event club, I lived curricular and extracurricular scenarios alike, including the cultural fest we organised every year, growing from an executive to an event head. There were multiple hackathons inside the college itself, plus a stretch with E-cell where things sat in a piled-up situation. Through it all, it meant all-round development for the future journey.',
            },
            {
                label: 'When the World Paused',
                text: 'Then COVID happened. College moved home: not much to live for, but a different scenario entirely. People came back, everyone started to reunite, bringing their thoughts and abilities from different places. From then on, the journey started, including an early internship at SaaSpect, learning technical architecture, DevOps engineering and exactly how software works.',
            },
            {
                label: 'The Specialization Shift',
                text: 'I specialised in ML, AI and data engineering. At that time, getting a job in AI/ML was not an adamant situation for me, so I shifted to testing, became a QA, and moved into data. The first full-time journey began.',
            },
        ],
        tags: ['Chitkara University', 'ML · AI · Data Engineering', 'Founding Member · Event Club', 'Executive → Event Head', 'COVID Era'],
        accent: '#00E676',
    },
    {
        id: 'saaspect',
        stage: '03',
        title: 'SaaSpect Internship',
        yearLabel: 'Early Internship · DevOps & Cloud',
        building: 'Cloud Server Tower: layered racks reaching up',
        description:
            'The internship that ran alongside college and changed the axis: DevOps engineering at SaaSpect. AWS requirements, technical architecture and deployment pipelines, the layer under everything. It answered a question college could not: exactly how software actually works, end to end. I completed all the parts; when college kept hiccuping, it got less of the attention. Given the choice between budgeting a whole life and implementing a scenario into action, the implementation won.',
        tags: ['AWS Infrastructure', 'DevOps Pipelines', 'Technical Architecture', 'Cloud Testing'],
        accent: '#22D3EE',
    },
    {
        id: 'the_pivot',
        stage: '04',
        title: 'The Move Ahead',
        yearLabel: '2022 · The Decision',
        building: 'Divergence Bridge: angled crossing off the main road',
        description:
            'Say it however you like: this was not a dropout. College wound down in 2022, with the DevOps internship already running alongside and a 16-mark backlog from second year that was supposed to convert, and did not. For a moment it felt like the journey and the career had been ruined. The answer was simpler than the setback: complete what was there, start doing things, and move ahead into testing, chosen deliberately after the AI/ML specialization, purely to explore other possibilities.',
        extras: [
            {
                label: 'The Testing Mindset',
                text: 'Testing introduced a mind the old developer mindset never had to hold: a product-side influence. Why this exists, who it fails, what it must never do. The same reverse-engineering instinct, through a completely different lens.',
            },
        ],
        tags: ['Not a Dropout', 'DevOps Internship', '2022', 'Exploring Possibilities'],
        accent: '#FFB300',
    },
    {
        id: 'keka',
        stage: '05',
        title: 'Keka Technologies',
        yearLabel: 'Jan 2022 – Dec 2023 · Intern → Senior Testing Engineer',
        building: 'Corporate Matrix: grid block office complex',
        description:
            'I started in January 2022 as an intern and was converted to full-time around August 2022. Here, software and the SaaS business stopped being theory: the culture, the daily bar, and multiple seniors who handed over the exact mindset a manual QA needs. After getting the hang of software testing came the methodologies and terminologies, the full SDLC vocabulary of smoke, regression, monkey and black-box testing. Then came the workforce-management department, handled as a senior testing engineer: customer-side issues, defining and designing the product, giving real input, and working closely with the product side and every team associated with us.',
        extras: [
            {
                label: 'Out of the Bubble',
                text: 'Different departments, cross-team functions, and how to handle things: many corporate cultures and ethical values. Two years here grew me out of the box and bubble I arrived in.',
            },
            {
                label: 'The Linear Graph',
                text: 'Growth is a linear graph, and a mistake of mine got rectified through further work and the journey ahead. Around December 2023, the decision landed: take everything Keka taught and put it toward better growth and a better path in the career.',
            },
            {
                label: 'The Automation Spark',
                text: 'Then the concept landed: automation testing, and how it frees all the mundane manual cases that recur every time a regression suite gets set up or regression testing runs. From there, the automation journey began, learned diligently.',
            },
            {
                label: 'Beyond the UI: APIs & Data',
                text: 'Alongside the suites: API testing, working with Postgres, SQL, MongoDB and all sorts of databases for Keka Sync2, a biometric integration with the ESSL devices.',
            },
        ],
        tags: ['First Corporate Experience', 'Intern → Full-Time · Aug 2022', 'Senior Testing Engineer', 'Workforce Management', 'Smoke · Regression · Monkey · Black-Box'],
        accent: '#34D399',
    },
    {
        id: 'toyow',
        stage: '06',
        title: 'TOYOW: DeFi & RWA Marketplace',
        yearLabel: 'April 2024 · Sole Test Engineer → Test Lead',
        building: 'On-Chain Vault: rotating ledger cube',
        description:
            'I landed in April 2024 as the sole test engineer for a brand-new tech stack: a DeFi marketplace tokenizing real-world commodities. Blockchain, smart contracts, on-chain transactional flows and ledger systems were all new, all learned from the ground up. I took responsibility for designing the test scenarios and test cases and for implementing every test deliverable from scratch, taking it to the enhanced level we operate at. In a small startup ecosystem, cross-team communication was essential, and the ownership was bigger: end-to-end quality on one platform, checked from the start, by me. From there, the journey turned toward every leadership role the Test Lead position demanded.',
        extras: [
            {
                label: 'The 4-Month Repurpose',
                text: 'After Keka came four unemployed months, used fully. I self-taught product management and how to develop a business, linearly and from open free sources: how to do a case study and what factors matter. I gave daily PM and APM interviews where the only flaw was experience. Changing domains is not easy, so I pursued what I was affluent in: test engineering.',
            },
            {
                label: 'The Automation Arsenal',
                text: 'I became well-versed in Selenium and pytest, with a real test suite built, and creating that suite aligned me closely with my product journey. Load testing, performance testing and benchmarking came next, along with every new tool arriving for automating test cases.',
            },
            {
                label: 'What Got Shipped',
                text: 'TOYOW tokens (an on-chain coin), plus admin portals, the marketplace, a token marketplace, OTC systems, and partner and issuer portals, all complexly integrated across every dynamic, workflow and environment. Compliance requirements for running a DeFi marketplace and app, and the Web3 domain, became part of the job.',
            },
        ],
        tags: ['DeFi / RWA Marketplace', 'Smart Contracts', 'On-Chain Ledger', 'TOYOW Token · Web3 Compliance', 'Sole QA → Test Lead'],
        accent: '#8B5CF6',
    },
    {
        id: 'lmn8',
        stage: '07',
        title: 'lmn8: Softra Globus Technologies',
        yearLabel: 'May 2026 · Part-Time · Parallel Track',
        building: 'Parallel Annex: a side structure running alongside the path',
        description:
            'The journey is not a single track. Alongside full-time TOYOW and the Worklyne build, I joined Softra Globus Technologies part-time in May 2026, working on their product, lmn8. Part-time is not less; it is a second stage running in parallel: more product exposure, more shipping, and living proof that momentum compounds when you run multiple tracks at once.',
        tags: ['Softra Globus Technologies', 'Part-Time', 'Since May 2026', 'Parallel Track'],
        accent: '#2DD4BF',
    },
    {
        id: 'worklyne',
        stage: '08',
        title: 'Worklyne: Work Management Platform',
        yearLabel: '~1 Year Building · CEO & Co-Founder',
        building: 'THE PEAK CITADEL: golden beacon, scaffolding open for more',
        description:
            'Every gap felt across years of corporate life, from time loss and low accountability to zero visibility and context drowning between follow-ups. At TOYOW, the fragmented space of tools made it concrete. Collaboratively, the Keka and TOYOW experience gave birth to Worklyne, built with my co-founder Arbaaz: a unified workspace of chat, CRM boards, markdown docs and task management with HR policy built in, made for SMEs to enterprises. One year in, it is now in private beta, with AI governance and a real transparency layer at the core. No stopping from here.',
        extras: [
            {
                label: 'The Personal Lab',
                text: 'AI runs through my daily life, automating most of the mundane work, with an enthusiastic knack for learning every new AI technology. It started with prompting; now I love creating products where I can innovate with AI on multiple bases. I even created my own version of Mirofish, and I am currently creating a place where AIs can talk to each other. My different AI tools hold separate silos of context, and every time one usage limit hits the cap, I have to re-define what I was working on another day. That is a genuine problem, so I am building the fix for myself, at the personal and individual level.',
            },
        ],
        tags: ['CEO & Co-Founder', 'Co-Founder: Arbaaz', 'Private Beta', 'Unified Workspace', 'SME → Enterprise OS', 'AI Governance', 'Transparency Layer'],
        link: { label: 'Visit Worklyne', href: 'https://[your-worklyne-link]' },
        accent: '#F59E0B',
        isPeak: true,
        peakNote:
            'This is the peak so far, not the summit. The skyline above stays under construction.',
    },
];

export const quote = {
    text: 'What is thinner than water? It is knowledge. Because knowledge can be limitless and gained from anywhere.',
    punchline: 'You just need to shut up and show up.',
    author: 'Ujjwal Praveen',
    role: 'Test Lead @ TOYOW | CEO & Co-founder @ Worklyne',
};

export const skills = [
    {
        name: 'DeFi & Smart-Contract QA',
        category: 'Domain',
        level: 'Expert',
        desc: 'On-chain ledger validation, payment-gateway flows and real-world-asset tokenization, plus the compliance requirements of running a live DeFi marketplace and its own token.',
        accent: '#8B5CF6',
        span: 'md:col-span-3 lg:col-span-3',
    },
    {
        name: 'Automation & Framework Design',
        category: 'Testing',
        level: 'Architect',
        desc: 'Test automation infrastructure designed from zero: variables, applications and framework patterns for DeFi products.',
        accent: '#00F2FE',
        span: 'md:col-span-3 lg:col-span-3',
    },
    {
        name: 'AWS & DevOps',
        category: 'Cloud',
        level: 'Advanced',
        desc: 'Pipelines, infrastructure requirements and cloud-native thinking, first learned at SaaSpect.',
        accent: '#22D3EE',
        span: 'md:col-span-2 lg:col-span-2',
    },
    {
        name: 'HRMS & Enterprise Domain',
        category: 'Product',
        level: 'Veteran',
        desc: 'Two years of workforce management: payroll, attendance and hierarchy edge cases, where correctness is non-negotiable.',
        accent: '#34D399',
        span: 'md:col-span-2 lg:col-span-2',
    },
    {
        name: 'SDLC & Test Methodology',
        category: 'Methodology',
        level: 'Master',
        desc: 'Smoke, regression, monkey and black-box testing: full lifecycle vocabulary applied across two very different industries.',
        accent: '#FFB300',
        span: 'md:col-span-2 lg:col-span-2',
    },
    {
        name: 'ML · AI · Data Engineering',
        category: 'Specialization',
        level: 'Studied',
        desc: 'The Chitkara specialization. When the AI/ML market was not adamant, the data thinking converted into a testing career instead of ending.',
        accent: '#00E676',
        span: 'md:col-span-2 lg:col-span-2',
    },
    {
        name: 'Mobile App Testing & Automation',
        category: 'Testing',
        level: 'Versatile',
        desc: 'Well-versed in mobile app testing and automation using APM, extending the QA stack beyond the web.',
        accent: '#22D3EE',
        span: 'md:col-span-2 lg:col-span-2',
    },
    {
        name: 'AI Tooling & Innovation',
        category: 'AI',
        level: 'Builder',
        desc: 'AI automates most of my mundane work, from the prompting era to building. I created my own version of Mirofish, and now a place where AIs talk to each other.',
        accent: '#00F2FE',
        span: 'md:col-span-2 lg:col-span-2',
    },
    {
        name: 'Product Building & AI Governance',
        category: 'Leadership',
        level: 'Founder',
        desc: 'CEO and co-founder of Worklyne: a unified workspace of CRM boards, chat, docs and task management with HR policy on each side, plus the AI-governance and compliance concerns every organisation is about to face.',
        accent: '#F59E0B',
        span: 'md:col-span-6 lg:col-span-6',
    },
];

export const hobbies = [
    {
        title: 'Creative Exploration',
        value: 'I love to free my mind from all the burnout, or fire up my brain nerves, by being spiritually active, and I love to explore nature.',
        note: 'spiritual · nature',
    },
    {
        title: 'Tactical Learning',
        value: 'I love playing FIFA and table tennis. It keeps my body active and my mind fresh, ready to tackle another situation, whether it is related to work or my personal life.',
        note: 'FIFA · table tennis',
    },
    {
        title: 'Unwinding & Craft',
        value: 'These are things I have been doing for a very long time: I love to paint and play guitar, and I enjoy reading books. My music taste is diverse too, sometimes metalhead and sometimes indie.',
        note: 'paint · guitar · books · metal & indie',
    },
];

export const contact = {
    email: 'ujjwal@worklyne.app',
    worklyne: 'https://www.worklyne.app',
    linkedin: 'https://www.linkedin.com/in/ujjwal-praveen',
    instagram: 'https://www.instagram.com/ujjwal.worklyne',
    resumePath: '/Ujjwal_Praveen_Resume.pdf',
};

export const marqueeItems = [
    'Manual QA → Automation',
    'DeFi & Smart Contracts',
    'AWS & DevOps',
    'HRMS Domain Logic',
    'Framework Design',
    'Sole Test Engineer',
    'Product Founder',
    'lmn8 · Parallel Track',
    'Knowledge < Water',
];
